import { useEffectOnce } from 'react-use';
import { RateInformations, useOfferBuilderContext } from '@/enterprise/components/OfferBuilder/OfferBuilderContext';
import { PRICING_RATE_TYPE_MAP } from '@/enterprise/components/OfferBuilder/utils';
import usePricingSuggestion, { type CombinedPricingSuggestion } from '@/enterprise/data/usePricingSuggestion';

/**
 * Refetches fresh pricing for PREFERRED and EXPRESS_ACCEPT actionable offer drafts.
 *
 * Passes pricingModifiers from the draft to ensure correct rate bumping is applied
 * when displaying offers as part of a 'preferred upsell' campaign.
 */
const HydrateDraftRates = () => {
  const {
    dispatch,
    state: { selected, frequency, draftOfferProperties },
  } = useOfferBuilderContext();

  const { draftType, offerType, pricingModifiers: draftModifiers } = draftOfferProperties ?? {};
  const isActionable = draftType === 'ACTIONABLE_OFFER';
  const isPreferred = offerType === 'PREFERRED';
  const isExpress = offerType === 'EXPRESS_ACCEPT';

  // Always explicitly set preferredCompetitionBump so SEA knows whether to apply rate bumping
  const pricingModifiers = {
    preferredCompetitionBump: draftModifiers?.preferredCompetitionBump ?? false,
  };

  const { data: pricingData } = usePricingSuggestion({
    takerMarkets: selected,
    isActionableOffer: isActionable,
    includePreferred: isPreferred,
    pricingModifiers,
  });

  const rateType = pricingData?.overallOptimalRateType;

  useEffectOnce(() => {
    if (!draftOfferProperties || !pricingData || !frequency) return;

    const getMarketPricing = (marketUuid: string, divisionUuid: string) =>
      pricingData.supplierMarkets.find((p) => p.marketUuid === marketUuid && p.supplierDivisionUuid === divisionUuid);

    // PREFERRED rates come from preferred.oneTime structure
    const getPreferredRate = (marketPricing: CombinedPricingSuggestion['supplierMarkets'][number]) => {
      const rate = marketPricing?.preferred?.oneTime;
      if (!rate?.rate || !rate?.rateType) return null;
      return {
        rate: rate.rate,
        rateType: rate.rateType as 'APR' | 'DISC',
      };
    };

    // EXPRESS_ACCEPT rates come from apr/discount.expressAccept.trendingRate.
    // When preferredCompetitionBump is true, force APR to match PREFERRED comparison.
    const getExpressRate = (marketPricing: CombinedPricingSuggestion['supplierMarkets'][number]) => {
      const resolvedRateType = pricingModifiers.preferredCompetitionBump ? 'APR' : (rateType as 'APR' | 'DISC');
      if (!resolvedRateType) return null;

      const key = PRICING_RATE_TYPE_MAP[resolvedRateType];
      const suggestion = marketPricing?.[key]?.expressAccept;
      if (!suggestion?.trendingRate) return null;

      return {
        rate: suggestion.trendingRate,
        rateType: resolvedRateType,
      };
    };

    const rateInformations: RateInformations[] = selected
      .map((market) => {
        const marketPricing = getMarketPricing(market.marketUuid, market.takerDivisionUuid);
        if (!marketPricing) return null;

        const rateInfo = isPreferred
          ? getPreferredRate(marketPricing)
          : isExpress
          ? getExpressRate(marketPricing)
          : null;

        if (!rateInfo) return null;

        return {
          marketUuid: market.marketUuid,
          supplierDivisionUuid: market.takerDivisionUuid,
          rateInformation: rateInfo,
        };
      })
      .filter(Boolean) as RateInformations[];

    if (!rateInformations.length) return;

    dispatch({
      type: 'HYDRATE_DRAFT_OFFER_RATE_INFORMATIONS',
      payload: {
        rateInformations,
        rateType: rateInformations[0].rateInformation.rateType as 'APR' | 'DISC',
      },
    });
  });

  return null;
};

export default HydrateDraftRates;
